/**
 * useDocumentRegistry Hook
 *
 * Hook for registering documents when printing.
 * Registers the document with the backend and returns a verification code
 * that can be embedded in a QR code.
 *
 * Usage:
 *   const { registerDocument, verificationCode, loading, error } = useDocumentRegistry()
 *
 *   // Register when ready to print
 *   await registerDocument('job_card', 'JC000001', 123, documentData)
 *
 *   // Use verificationCode in DocumentQRCode component
 */

import { useState, useCallback } from 'react'
import api from '../services/api'

export const useDocumentRegistry = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [registryData, setRegistryData] = useState(null)

  /**
   * Register a document for verification
   *
   * @param {string} documentType - Type of document (job_card, issue_voucher, vehicle_request, pm_schedule, list_export)
   * @param {string} documentNumber - Document number or title
   * @param {number|null} objectId - ID of the related object (null for list exports)
   * @param {object} documentData - Full document data for hash calculation
   * @param {object} exportMetadata - Optional metadata for list exports
   * @returns {Promise<object>} Registry data including verification_code
   */
  const registerDocument = useCallback(
    async (documentType, documentNumber, objectId, documentData, exportMetadata = {}) => {
      setLoading(true)
      setError(null)

      try {
        const response = await api.post('/common/document-registry/register/', {
          document_type: documentType,
          document_number: documentNumber,
          object_id: objectId,
          document_data: documentData,
          export_metadata: exportMetadata,
        })

        setRegistryData(response.data)
        return response.data
      } catch (err) {
        const errorMsg =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          'Failed to register document'
        setError(errorMsg)
        console.error('Document registration failed:', err)
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Clear the current registration data
   */
  const clearRegistry = useCallback(() => {
    setRegistryData(null)
    setError(null)
  }, [])

  return {
    registerDocument,
    clearRegistry,
    loading,
    error,
    registryData,
    verificationCode: registryData?.verification_code,
    verificationUrl: registryData?.verification_url,
    alreadyRegistered: registryData?.already_registered,
  }
}

export default useDocumentRegistry
